
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Search,
  ArrowLeft, 
  Download, 
  Eye,
  FileText,
  DollarSign,
  Clock,
  CheckCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../store/store';
import { 
  fetchInvoices, 
  searchInvoices,
  selectInvoices,
  selectInvoicesLoading,
  selectInvoicesSearching,
  selectInvoicesError,
  selectSearchTerm,
  selectSelectedStatusFilter,
  selectInvoicesTotal,
  setSearchTerm,
  setSelectedStatusFilter
} from '../store/slices/invoicesSlice';
import { selectCurrentClinic, selectClinicsLoading } from '../store/slices/authSlice';
import ResponsiveNavigation from '@/components/ResponsiveNavigation';

const Billing = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const currentClinic = useSelector(selectCurrentClinic);
  const clinicsLoading = useSelector(selectClinicsLoading);
  const invoices = useSelector(selectInvoices);
  const loading = useSelector(selectInvoicesLoading);
  const searching = useSelector(selectInvoicesSearching);
  const error = useSelector(selectInvoicesError);
  const searchTerm = useSelector(selectSearchTerm);
  const selectedStatusFilter = useSelector(selectSelectedStatusFilter);
  const total = useSelector(selectInvoicesTotal);

  // Load invoices when component mounts or clinic changes
  useEffect(() => {
    // GATE: Wait for clinic loading to complete
    if (clinicsLoading) {
      console.log('[Billing] Waiting for clinics to load...');
      return;
    }

    // GATE: Ensure we have clinic data with invoices href
    if (!currentClinic?.invoices?.href) {
      console.log('[Billing] No invoices href available yet');
      return;
    }

    // NOW SAFE: Make API call
    console.log('[Billing] Loading invoices from:', currentClinic.invoices.href);
    dispatch(fetchInvoices({}));
  }, [dispatch, currentClinic, clinicsLoading]);

  // Handle search term changes
  const handleSearchChange = (value: string) => {
    dispatch(setSearchTerm(value));
    if (value.trim() && currentClinic?.invoices?.href) {
      dispatch(searchInvoices({ invoicesHref: currentClinic.invoices.href, searchTerm: value.trim() }));
    } else if (currentClinic?.invoices?.href) {
      dispatch(fetchInvoices({}));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in collection':
        return 'bg-orange-100 text-orange-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'in collection':
        return <FileText className="w-4 h-4" />;
      case 'paid':
        return <CheckCircle className="w-4 h-4" />;
      case 'overdue':
        return <Clock className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  // Filter invoices based on current filters (client-side filtering for now)
  const filteredInvoices = invoices.filter(invoice => {
    const matchesStatus = selectedStatusFilter.length === 0 || selectedStatusFilter.includes(invoice.status);
    return matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gray-50 font-lato">
      <ResponsiveNavigation />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button and Title */}
        <div className="flex items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mr-4 text-vitl-navy hover:text-vitl-green font-lato"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-syne font-medium text-vitl-navy">Billing & Invoices</h1>
        </div>

        {/* Search Bar */}
        <div className="relative mb-6">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-vitl-dark/50" />
          <Input
            type="text"
            placeholder="Search invoices, amounts, or dates"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10 h-12 bg-gray-100 border-0 focus:ring-2 focus:ring-vitl-green/20 focus:border-vitl-green font-lato"
          />
        </div>

        {/* Invoice Tabs */}
        <Tabs defaultValue="all-invoices" className="mb-6">
          <TabsList className="grid w-full grid-cols-3 bg-white border">
            <TabsTrigger value="all-invoices" className="data-[state=active]:bg-vitl-green data-[state=active]:text-white font-lato">
              All Invoices ({total})
            </TabsTrigger>
            <TabsTrigger value="open" className="data-[state=active]:bg-vitl-green data-[state=active]:text-white font-lato">
              Open Invoices
            </TabsTrigger>
            <TabsTrigger value="paid" className="data-[state=active]:bg-vitl-green data-[state=active]:text-white font-lato">
              Paid Invoices
            </TabsTrigger>
          </TabsList>

          {/* Loading State - show during clinic loading OR invoices loading */}
          {(loading || searching || clinicsLoading) && (
            <div className="text-center py-12">
              <p className="text-vitl-dark/70 font-lato">
                {searching ? 'Searching...' : 
                 clinicsLoading ? 'Loading clinic data...' : 
                 'Loading invoices...'}
              </p>
            </div>
          )}

          {/* Error State */}
          {error && !clinicsLoading && (
            <div className="text-center py-12">
              <p className="text-red-600 font-lato">Error: {error}</p>
            </div>
          )}

          {/* All Invoices Tab */}
          <TabsContent value="all-invoices">
            {!loading && !searching && !error && !clinicsLoading && (
              <div className="space-y-4">
                {filteredInvoices.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-vitl-dark/70 font-lato">No invoices found matching your filters.</p>
                  </div>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <Card key={invoice.id} className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-all duration-200 shadow-emerald-100/50">
                      <CardContent className="p-6">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
                          <div className="lg:col-span-2">
                            <p className="font-lato font-medium text-vitl-navy text-sm">{invoice.id.slice(-8)}</p>
                          </div>
                          
                          <div className="lg:col-span-2">
                            <div>
                              <p className="text-vitl-dark/50 text-xs font-lato">Issued</p>
                              <p className="text-vitl-navy font-lato text-sm">{new Date(invoice.issued_date).toLocaleDateString()}</p>
                            </div>
                          </div>

                          <div className="lg:col-span-2">
                            <div>
                              <p className="text-vitl-dark/50 text-xs font-lato">Due Date</p>
                              <p className="text-vitl-navy font-lato text-sm">{new Date(invoice.due_date).toLocaleDateString()}</p>
                            </div>
                          </div>

                          <div className="lg:col-span-2">
                            <div>
                              <p className="text-vitl-dark/50 text-xs font-lato">Amount Due</p>
                              <p className="text-vitl-navy font-lato text-lg font-medium">${(invoice.amount_due / 100).toFixed(2)}</p>
                            </div>
                          </div>

                          <div className="lg:col-span-2">
                            <div>
                              <p className="text-vitl-dark/50 text-xs font-lato">Amount Paid</p>
                              <p className="text-vitl-navy font-lato text-lg font-medium">${(invoice.amount_paid / 100).toFixed(2)}</p>
                            </div>
                          </div>

                          <div className="lg:col-span-1">
                            <span className={`${getStatusColor(invoice.status)} px-2 py-1 text-xs font-lato rounded border flex items-center space-x-1`}>
                              {getStatusIcon(invoice.status)}
                              <span className="capitalize">{invoice.status}</span>
                            </span>
                          </div>

                          <div className="lg:col-span-1 text-right">
                            <div className="flex space-x-2 justify-end">
                              <Button variant="ghost" size="sm" className="text-vitl-navy hover:text-vitl-green">
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-vitl-navy hover:text-vitl-green">
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}
          </TabsContent>

          {/* Open Invoices Tab */}
          <TabsContent value="open">
            {!loading && !searching && !error && !clinicsLoading && (
              <div className="space-y-4">
                {filteredInvoices.filter(invoice => ['Pending', 'In Collection', 'Overdue'].includes(invoice.status)).map((invoice) => (
                  <Card key={invoice.id} className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-all duration-200 shadow-emerald-100/50">
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
                        <div className="lg:col-span-2">
                          <p className="font-lato font-medium text-vitl-navy text-sm">{invoice.id.slice(-8)}</p>
                        </div>
                        
                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Issued</p>
                            <p className="text-vitl-navy font-lato text-sm">{new Date(invoice.issued_date).toLocaleDateString()}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Due Date</p>
                            <p className="text-vitl-navy font-lato text-sm">{new Date(invoice.due_date).toLocaleDateString()}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Amount Due</p>
                            <p className="text-vitl-navy font-lato text-lg font-medium">${(invoice.amount_due / 100).toFixed(2)}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Amount Paid</p>
                            <p className="text-vitl-navy font-lato text-lg font-medium">${(invoice.amount_paid / 100).toFixed(2)}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-1">
                          <span className={`${getStatusColor(invoice.status)} px-2 py-1 text-xs font-lato rounded border flex items-center space-x-1`}>
                            {getStatusIcon(invoice.status)}
                            <span className="capitalize">{invoice.status}</span>
                          </span>
                        </div>

                        <div className="lg:col-span-1 text-right">
                          <div className="flex space-x-2 justify-end">
                            <Button variant="ghost" size="sm" className="text-vitl-navy hover:text-vitl-green">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-vitl-navy hover:text-vitl-green">
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Paid Invoices Tab */}
          <TabsContent value="paid">
            {!loading && !searching && !error && !clinicsLoading && (
              <div className="space-y-4">
                {filteredInvoices.filter(invoice => invoice.status === 'Paid').map((invoice) => (
                  <Card key={invoice.id} className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-all duration-200 shadow-emerald-100/50">
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
                        <div className="lg:col-span-2">
                          <p className="font-lato font-medium text-vitl-navy text-sm">{invoice.id.slice(-8)}</p>
                        </div>
                        
                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Issued</p>
                            <p className="text-vitl-navy font-lato text-sm">{new Date(invoice.issued_date).toLocaleDateString()}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Due Date</p>
                            <p className="text-vitl-navy font-lato text-sm">{new Date(invoice.due_date).toLocaleDateString()}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Amount Due</p>
                            <p className="text-vitl-navy font-lato text-lg font-medium">${(invoice.amount_due / 100).toFixed(2)}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Amount Paid</p>
                            <p className="text-vitl-navy font-lato text-lg font-medium">${(invoice.amount_paid / 100).toFixed(2)}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-1">
                          <span className={`${getStatusColor(invoice.status)} px-2 py-1 text-xs font-lato rounded border flex items-center space-x-1`}>
                            {getStatusIcon(invoice.status)}
                            <span className="capitalize">{invoice.status}</span>
                          </span>
                        </div>

                        <div className="lg:col-span-1 text-right">
                          <div className="flex space-x-2 justify-end">
                            <Button variant="ghost" size="sm" className="text-vitl-navy hover:text-vitl-green">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-vitl-navy hover:text-vitl-green">
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Billing;
